import { BordLateralModal } from 'bord-design-system';
import { BordOneToneIcon } from 'components/NudosComponents';
import { IHistoryGroup, IQuoteHistory, IQuoteStatus, QUOTE_STATUS_CODE } from 'types/quoteDetails.types';
import './QuoteDetailHistoryModal.scss';

interface IQuoteDetailHistoryModal {
  open: boolean;
  onClose: () => void;
  quoteStatus?: IQuoteStatus;
  history?: IQuoteHistory;
}

const EMPTY_HISTORY: IQuoteHistory = {
  cancelled: [],
  expired: [],
  approved: [],
  pendingApproval: [],
  toQuote: []
};

/** Orden de pasos del historial de menor a mayor avance. */
const historyStepOrder = [
  QUOTE_STATUS_CODE.TO_QUOTE,
  QUOTE_STATUS_CODE.PENDING_APPROVAL,
  QUOTE_STATUS_CODE.PURCHASED
];

/**
 * Modal lateral de historial de la cotización.
 * Construye los grupos a partir del `history` real: el grupo superior toma los items de
 * `approved`, `expired` o `cancelled` según el `quoteStatus`, y los otros grupos toman
 * `pendingApproval` y `toQuote` respectivamente.
 */
const QuoteDetailHistoryModal = ({ open, onClose, quoteStatus, history }: IQuoteDetailHistoryModal) => {
  const safeHistory = history ?? EMPTY_HISTORY;
  const currentCode = quoteStatus?.code;

  const normalizedCode =
    currentCode === QUOTE_STATUS_CODE.PENDING
      ? QUOTE_STATUS_CODE.TO_QUOTE
      : currentCode === QUOTE_STATUS_CODE.APPROVED
      ? QUOTE_STATUS_CODE.PURCHASED
      : currentCode;

  const topItems =
    currentCode === QUOTE_STATUS_CODE.EXPIRED
      ? safeHistory.expired
      : currentCode === QUOTE_STATUS_CODE.REJECTED
      ? safeHistory.cancelled
      : safeHistory.approved;

  const historyGroups: IHistoryGroup[] = [
    { statusId: QUOTE_STATUS_CODE.PURCHASED, label: 'Aprobado', items: topItems },
    { statusId: QUOTE_STATUS_CODE.PENDING_APPROVAL, label: 'Pendiente de aprobación', items: safeHistory.pendingApproval },
    { statusId: QUOTE_STATUS_CODE.TO_QUOTE, label: 'Por cotizar', items: safeHistory.toQuote }
  ];

  const leadingIcon = (
    <div className="historyLeadingIcon">
      <BordOneToneIcon variant="clock" standardSize={24} stroke="#6F737D" />
    </div>
  );

  const modalContent = (
    <>
      <hr className="dividerHistoryModal" />
      <div className="historyGroups">
        {historyGroups.map((group, groupIndex) => {
          const isTerminalStatus =
            normalizedCode === QUOTE_STATUS_CODE.EXPIRED ||
            normalizedCode === QUOTE_STATUS_CODE.REJECTED;
          const currentStepIndex = isTerminalStatus
            ? historyStepOrder.length
            : normalizedCode
            ? historyStepOrder.indexOf(normalizedCode as QUOTE_STATUS_CODE)
            : -1;
          const groupStepIndex = historyStepOrder.indexOf(group.statusId);
          const isReached = normalizedCode !== undefined && groupStepIndex <= currentStepIndex;
          const isCurrentStep = normalizedCode !== undefined && group.statusId === normalizedCode;
          const isApprovedFinal = normalizedCode === QUOTE_STATUS_CODE.PURCHASED;
          const isTerminalOverride = group.statusId === QUOTE_STATUS_CODE.PURCHASED && isTerminalStatus;
          const isTerminalGray = isTerminalStatus && !isTerminalOverride;
          const isCurrent = isCurrentStep || isTerminalOverride;
          const showLoader = isCurrent && !isApprovedFinal && !isTerminalOverride;
          const stateClass = isCurrent
            ? `current${isApprovedFinal ? ' approvedFinal' : ''}${isTerminalOverride ? ' terminal' : ''}`
            : isTerminalGray
            ? 'pending'
            : isReached
            ? 'reached'
            : 'pending';
          const iconVariant = isTerminalStatus ? 'x' : showLoader ? 'loader' : isReached ? 'check' : 'clock';
          const iconStroke = isTerminalOverride
            ? '#DE072E'
            : isTerminalGray
            ? '#6F737D'
            : showLoader
            ? '#23A5FF'
            : isReached
            ? '#00994F'
            : '#6F737D';
          const iconSize = showLoader ? 16 : isReached ? 14 : 16;
          const terminalLabel = normalizedCode === QUOTE_STATUS_CODE.EXPIRED ? 'Vencido' : 'Cancelada';
          const groupLabel = isTerminalOverride ? terminalLabel : group.label;
          const mutedClass = isTerminalGray ? ' muted' : '';
          return (
            <div key={groupIndex} className="historyGroup">
              <div className="groupHeader">
                <div className={`groupCheckIcon ${stateClass}`}>
                  <div className="groupCheckIconInner">
                    <BordOneToneIcon variant={iconVariant} standardSize={iconSize} stroke={iconStroke} />
                  </div>
                </div>
                <span className={`groupLabel${isTerminalOverride ? ' terminal' : ''}${mutedClass}`}>
                  {groupLabel}
                </span>
              </div>
              {isReached && group.items.length > 0 && (
                <div className="groupBody">
                  <div className="groupItems">
                    {group.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className={`historyItem${itemIndex === group.items.length - 1 ? ' last' : ''}${mutedClass}`}
                      >
                        <div className={`itemBullet${mutedClass}${group.items.length === 1 ? ' invisible' : ''}`} />
                        <div className="itemContent">
                          <div className="itemHeader">
                            <span className={`itemType${mutedClass}`}>{item.type}</span>
                            <span className={`itemDate${mutedClass}`}>{item.date}</span>
                          </div>
                          <div className={`itemDescription${mutedClass}`}>{item.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <BordLateralModal
      open={open}
      onClose={onClose}
      bordLateralModalHeaderProps={{
        title: 'Historial',
        subtitle: 'Proceso por el que ha pasado la cotización',
        leading: leadingIcon,
        onClose
      }}
      className="customBordLateralModalClassName"
      bordLateralModalContent={modalContent}
      panelClassName="customPanelClassName"
    />
  );
};

export default QuoteDetailHistoryModal;
